// Libraries
import React from 'react';

// Supermove
import {DataLoader, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {
  DocumentItemInputChargePaymentType,
  OrganizationModel,
  PaymentMethodModel,
  UserModel,
} from '@supermove/models';

// App
import Text from '@shared/design/components/Text';
import {convertAmountKindAndAmountToText} from '@shared/modules/Document/components/DocumentV2ChargePaymentInputEdit';

const LoadingIndicator = Styled.Loading`
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const DocumentV2ChargePaymentInputNoPaymentMethod = ({
  organization,
}: {
  organization?: OrganizationModel;
}) => {
  return (
    <React.Fragment>
      <Text.Body>
        Please contact {organization?.name || 'the office'} to make your payment.
      </Text.Body>
    </React.Fragment>
  );
};

const DocumentV2ChargePaymentInputBodyView = ({
  metadata,
  paymentMethod,
}: {
  metadata: DocumentItemInputChargePaymentType['input']['metadata'];
  paymentMethod: PaymentMethodModel;
}) => {
  return (
    <React.Fragment>
      <Text.Subheading>Payment Method</Text.Subheading>
      <Space height={8} />
      <Row>
        <Text.Label>{paymentMethod.name}</Text.Label>
        <Space width={32} />
        <Text.Body>Visa •••• 0000</Text.Body>
      </Row>
      <Space height={8} />
      <Row>
        <Text.Label>Payment Total</Text.Label>
        <Space width={32} />
        <Text.Body>
          {convertAmountKindAndAmountToText(
            metadata.amountKind,
            metadata.amountFixedInCents,
            metadata.amountPercentageInFraction,
          )}
        </Text.Body>
      </Row>
    </React.Fragment>
  );
};

const DocumentV2ChargePaymentInputView = ({
  input,
}: {
  input: DocumentItemInputChargePaymentType['input'];
}) => {
  const {paymentMethodIds} = input.metadata;
  const paymentMethodId = paymentMethodIds.length > 0 ? paymentMethodIds[0] : null;

  const {data, loading} = useQuery<{
    viewer: UserModel;
  }>(DocumentV2ChargePaymentInputView.query, {
    variables: {paymentMethodId},
    skip: !paymentMethodId,
  });

  return (
    <DataLoader data={data} loading={loading} as={LoadingIndicator}>
      {({loadedData}) =>
        loadedData?.viewer.viewingOrganization.paymentMethod ? (
          <DocumentV2ChargePaymentInputBodyView
            metadata={input.metadata}
            paymentMethod={loadedData.viewer.viewingOrganization.paymentMethod}
          />
        ) : (
          <DocumentV2ChargePaymentInputNoPaymentMethod
            organization={loadedData?.viewer.viewingOrganization}
          />
        )
      }
    </DataLoader>
  );
};

DocumentV2ChargePaymentInputView.query = gql`
  query DocumentV2ChargePaymentInputView($paymentMethodId: Int!) {
    viewer {
      id
      viewingOrganization {
        id
        paymentMethod(paymentMethodId: $paymentMethodId) {
          id
          kind
          name
        }
      }
    }
  }
`;

export default DocumentV2ChargePaymentInputView;
